import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import React, {
  useEffect,
  useMemo,
  useReducer,
  useState,
  useContext
} from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import { convertGlobalIdToRawId } from '@bombas/shopify';
import { ApplicationContext } from 'shop/context/ApplicationContext';
import { REBUY_CART_UPSELL_ID } from 'shop/lib/util/const';
import hive from '@bombas/hive';
import PubSub from 'pubsub-js';
import { RebuyUpsellWithData } from '../../../RebuyUpsell/RebuyUpsell';
import { PRODUCT_VARIANT_PIM_DATA } from '../../Cart.gql';

import { vercel_base_url } from '../../../../util/config';
import { BUILD } from '../../../../util/const';
import { beginLoopCheckout, isLoopOnStore } from '../../../../util/loopReturns';
import { useIdentity } from '../../../Authentication/Authentication.hooks';
import DialogHeader from '../../../DialogHeader/DialogHeader';
import GiftMessageDialog from '../../../GiftMessageDialog/GiftMessageDialog';
import GiftOptionsDialog from '../../../GiftOptionsDialog/GiftOptionsDialog';
import ButtonIcon from '../../../Icons/ButtonIcon';
import ShopButtons from '../../../ShopButtons/ShopButtons';
import CartPropTypes from '../../Cart.propTypes';
import {
  initialState,
  UNDO_HIDE,
  UNDO_SHOW,
  undoStateReducer
} from '../../Cart.reducer';
import {
  calcTotalApplicableToFreeShippingThreshold,
  calcTotalCodeDiscountAmount,
  DIALOGS,
  fetchDiscountObject,
  formatPimData,
  getVariantCount,
  patchCartUrl,
  transformLineItemToProductVariantProps
} from '../../Cart.utils';
import { reorganizeCartWithQbsGroups } from '../../Cart.qbs.utils';
import { getCurrencySymbol } from '../../../../util/localize';

import * as Styled from './CartDialog.styled';
import QbsGroup from '../QbsGroup/QbsGroup';

const TRANSITION_DURATION = 300;
const { GIFT_OPTIONS, GIFT_MESSAGE } = DIALOGS;

/**
 * Renders a cart within a drawer.
 */
const CartDialog = React.forwardRef(
  (
    {
      cart,
      codeDiscountPercentage,
      freeShippingThreshold,
      freeShippingMethod,
      isOpen,
      onAddToCart,
      onRequestClose,
      onQuantityUpdate,
      prioritizedGiftingEnabled,
      setViewCouponDialogDisplay,
      shippingPolicy,
      siteConfig,
      upsellCollectionId,
      upsellSubtitle,
      upsellTitle,
      ...props
    },
    ref
  ) => {
    const [undoState, undoStateDispatch] = useReducer(
      undoStateReducer,
      initialState
    );
    const { qbsDiscountData } = useContext(ApplicationContext);

    const [hasFiltersOrSort, setHasFiltersOrSort] = useState(false);

    const isCartEmpty =
      !undoState.show && (cart?.lines?.edges.length || 0) <= 0;

    const discountObject = fetchDiscountObject(cart);

    const usableNetEnabled = Cookies.get('UsableNetAssistive');

    // fetches PIM Data for the variant upon undoing a variant removal from cart
    const [getProductVariantPimData, { error }] = useLazyQuery(
      PRODUCT_VARIANT_PIM_DATA,
      {
        onError: (err) => {
          window.DD_RUM?.onReady(() => {
            if (err.networkError) {
              window.DD_RUM?.addError(
                (new Error(err.networkError),
                {
                  location: 'cart',
                  type: 'network',
                  request: 'getProductVariantPIMData'
                })
              );
            }

            err.graphQLErrors.forEach((e) => {
              window.DD_RUM?.addError(new Error(e), {
                location: 'cart',
                type: 'graphQL',
                request: 'getProductVariantPIMData'
              });
            });
          });
        }
      }
    );

    const codeDiscountAmount = useMemo(
      () => calcTotalCodeDiscountAmount(cart?.discountAllocations),
      [cart]
    );

    const cartItemCount = useMemo(
      () => getVariantCount(cart?.lines?.edges),
      [cart]
    );

    const [dialogToDisplay, setDialogToDisplay] = useState(null);

    const { user } = useIdentity();

    const country = user?.locale?.country;

    const base_url = BUILD === 'shopify' ? vercel_base_url : '';

    useEffect(() => {
      let clearUndoState;
      if (undoState.show) {
        clearUndoState =
          !usableNetEnabled &&
          setTimeout(() => undoStateDispatch({ type: UNDO_HIDE }), 4000);
      }

      return () => {
        clearTimeout(clearUndoState);
      };
    }, [undoState, usableNetEnabled]);

    const cartItems = reorganizeCartWithQbsGroups(
      cart?.lines?.edges,
      qbsDiscountData
    );

    useEffect(() => {
      const checkFilters = () => {
        const params = new URLSearchParams(window.location.search);
        const hasFilters = params.has('filter') || params.has('sort');
        setHasFiltersOrSort(hasFilters);
      };

      checkFilters();

      const token = PubSub.subscribe('filterSort.updated', () => {
        checkFilters();
      });

      return () => {
        PubSub.unsubscribe(token);
      };
    }, []);

    return (
      <>
        <Styled.CartDialog
          data-testid="MiniCart"
          data-label="miniCart"
          isOpen={isOpen}
          isEmpty={isCartEmpty}
          hasBackdrop={false}
          onRequestClose={onRequestClose}
          data-track-region="minicart"
          {...props}
        >
          <DialogHeader
            title="Shopping Bag"
            leftComponent={
              <Styled.CartPageLink
                href={`${base_url}/cart`}
                as={BUILD === 'shopify' && 'a'}
              >
                <Styled.CartNotificationIcon
                  name="bag"
                  count={cartItemCount}
                  role="presentation"
                  tabIndex="-1"
                />
              </Styled.CartPageLink>
            }
            rightComponent={
              <ButtonIcon
                variant="defaultLight"
                name="close"
                onClick={onRequestClose}
                data-testid="CartDialog__close-button"
                data-track-label="close"
                aria-label="close mini bag"
                data-label="miniCart_close"
              />
            }
            withDivider={false}
          />
          <Styled.ScrollContainer ref={ref}>
            {['US', 'CA', 'AU', 'GB'].includes(country) && (
              <Styled.FreeShipping
                shippingPolicy={shippingPolicy}
                threshold={freeShippingThreshold}
                method={freeShippingMethod}
                total={calcTotalApplicableToFreeShippingThreshold(
                  cart?.lines?.edges,
                  codeDiscountAmount
                )}
                backgroundColor={convertColorToThemeHexCode('grey20')}
                currencyCode={cart?.cost?.subtotalAmount?.currencyCode}
                currencySymbol={getCurrencySymbol(country)}
              />
            )}

            {isCartEmpty && (
              <>
                <Styled.EmptyCartTitle>
                  Your bag is empty.
                </Styled.EmptyCartTitle>
                <ShopButtons
                  variant="secondary"
                  size="large"
                  hasLargeButtonGap
                />
              </>
            )}

            {!isCartEmpty && (
              <>
                <Styled.CartLineItems>
                  {prioritizedGiftingEnabled && (
                    <Styled.GiftMessageCardAdd
                      withImage={false}
                      title="Is this a gift?"
                      description="Add a gift message or gift wrap"
                      onClick={() => {
                        setDialogToDisplay(GIFT_OPTIONS);
                        hive.track('GIFT_OPTIONS_PROMPT_CLICK');
                      }}
                    />
                  )}
                  {cart.note && (
                    <Styled.GiftMessageCardEdit
                      note={cart.note}
                      onEditClick={() => setDialogToDisplay(GIFT_MESSAGE)}
                    />
                  )}
                  <CSSTransition
                    in={undoState.show}
                    unmountOnExit
                    classNames="fade"
                    timeout={TRANSITION_DURATION}
                  >
                    <Styled.ConfirmationCardWrapper
                      transitionDuration={TRANSITION_DURATION}
                    >
                      <Styled.ConfirmationCard
                        title={undoState.product?.title}
                        onUndo={async () => {
                          await onAddToCart({
                            variantId: undoState.product?.variantId,
                            quantity: undoState.product?.quantity,
                            pimData: undoState.product?.pimData,
                            productId: undoState.product?.productId,
                            source: 'Cart'
                          });
                          undoStateDispatch({ type: UNDO_HIDE });
                        }}
                      />
                    </Styled.ConfirmationCardWrapper>
                  </CSSTransition>
                  <TransitionGroup>
                    {cartItems.map((item) => {
                      if (item.type === 'qbs-group') {
                        return (
                          <CSSTransition
                            key={item.id}
                            timeout={TRANSITION_DURATION}
                            classNames="fade"
                          >
                            <Styled.ProductVariantWrapper
                              transitionDuration={TRANSITION_DURATION}
                            >
                              <QbsGroup
                                header={item.header}
                                groupItems={item.groupItems}
                                onQuantityUpdate={async (
                                  lineItemId,
                                  newQty
                                ) => {
                                  const originalLineItem = item.groupItems.find(
                                    (lineItem) =>
                                      lineItem.node.id === lineItemId
                                  );

                                  if (originalLineItem) {
                                    await onQuantityUpdate(
                                      lineItemId,
                                      newQty,
                                      originalLineItem.node.merchandise.id,
                                      originalLineItem.node.quantity
                                    );

                                    if (newQty === 0) {
                                      const variantId = convertGlobalIdToRawId(
                                        originalLineItem.node.merchandise.id
                                      );

                                      const productId = convertGlobalIdToRawId(
                                        originalLineItem.node.merchandise
                                          .product.id
                                      );

                                      const rawPimData =
                                        await getProductVariantPimData({
                                          variables: { ids: [variantId] }
                                        });

                                      undoStateDispatch({
                                        type: UNDO_SHOW,
                                        product: {
                                          variantId,
                                          productId,
                                          title:
                                            originalLineItem.node.merchandise
                                              .product.title,
                                          quantity:
                                            originalLineItem.node.quantity,
                                          sku: originalLineItem.node.merchandise
                                            .sku,
                                          pimData: error
                                            ? undefined
                                            : formatPimData(rawPimData)
                                        }
                                      });
                                    }
                                  }
                                }}
                              />
                            </Styled.ProductVariantWrapper>
                          </CSSTransition>
                        );
                      }

                      const lineItem = item;
                      const productVariantProps =
                        transformLineItemToProductVariantProps(lineItem);

                      return (
                        <CSSTransition
                          key={lineItem.node.id}
                          timeout={TRANSITION_DURATION}
                          classNames="fade"
                        >
                          <Styled.ProductVariantWrapper
                            transitionDuration={TRANSITION_DURATION}
                          >
                            <Styled.ProductVariant
                              {...productVariantProps}
                              onQuantityUpdate={(newQty) => {
                                onQuantityUpdate(
                                  lineItem.node.id,
                                  newQty,
                                  lineItem.node.merchandise.id,
                                  lineItem.node.quantity
                                ).then(async () => {
                                  if (newQty === 0) {
                                    const variantId = convertGlobalIdToRawId(
                                      lineItem.node.merchandise.id
                                    );

                                    const productId = convertGlobalIdToRawId(
                                      lineItem.node.merchandise.product.id
                                    );

                                    const rawPimData =
                                      await getProductVariantPimData({
                                        variables: { ids: [variantId] }
                                      });

                                    undoStateDispatch({
                                      type: UNDO_SHOW,
                                      product: {
                                        variantId,
                                        productId,
                                        title:
                                          lineItem.node.merchandise.product
                                            .title,
                                        quantity: lineItem.node.quantity,
                                        sku: lineItem.node.merchandise.sku,
                                        pimData: error
                                          ? undefined
                                          : formatPimData(rawPimData)
                                      }
                                    });
                                  }
                                });
                              }}
                            />
                          </Styled.ProductVariantWrapper>
                        </CSSTransition>
                      );
                    })}
                  </TransitionGroup>
                </Styled.CartLineItems>
                {cart.lines.edges.length > 0 && (
                  <RebuyUpsellWithData
                    withQuickView={!hasFiltersOrSort}
                    dataLabel="miniCart_upsell"
                    cartSubtotal={cart?.cost?.subtotalAmount?.amount}
                    productIds={cartItems.flatMap((item) => {
                      if (item.type === 'qbs-group') {
                        return item.groupItems.map((lineItem) =>
                          convertGlobalIdToRawId(
                            lineItem.node.merchandise.product.id
                          )
                        );
                      }
                      return [
                        convertGlobalIdToRawId(item.node.merchandise.product.id)
                      ];
                    })}
                    dataSourceId={REBUY_CART_UPSELL_ID}
                  />
                )}
              </>
            )}
          </Styled.ScrollContainer>

          <Styled.CartFooter
            showViewBagLink
            isGiftingPrioritized={prioritizedGiftingEnabled}
            onRequestClose={onRequestClose}
            currencyCode={cart?.cost?.subtotalAmount?.currencyCode}
            subtotalPrice={cart?.cost?.subtotalAmount?.amount}
            totalItems={cart?.lines?.edges.length}
            hasGiftableProduct={cart?.lines?.edges.some(
              (item) =>
                !item.node.merchandise.product.handle.includes('gift-card')
            )}
            discountCode={discountObject?.code}
            discountAmount={codeDiscountAmount}
            discountPercentage={codeDiscountPercentage}
            checkoutUrl={patchCartUrl(cart?.checkoutUrl)}
            onCheckoutClick={async (href) => {
              await hive.track('MINICART_CHECKOUT_CLICK');

              if (isLoopOnStore()) {
                const variantIds = [];
                cart.lines.edges.forEach((lineItem) => {
                  for (let i = 0; i < lineItem.node.quantity; i += 1) {
                    variantIds.push(
                      convertGlobalIdToRawId(lineItem.node.merchandise.id)
                    );
                  }
                });
                await beginLoopCheckout(variantIds);
              } else {
                window.location = href;
              }
            }}
            giftMessage={cart?.note}
            onGiftOptionsClick={() => setDialogToDisplay(GIFT_OPTIONS)}
            siteConfig={siteConfig}
            setViewCouponDialogDisplay={setViewCouponDialogDisplay}
          />
        </Styled.CartDialog>

        <GiftOptionsDialog
          isOpen={dialogToDisplay === GIFT_OPTIONS}
          upsellCollectionId={upsellCollectionId}
          upsellTitle={upsellTitle}
          upsellSubtitle={upsellSubtitle}
          note={cart?.note}
          onCloseClick={() => setDialogToDisplay(null)}
        />

        <GiftMessageDialog
          isOpen={dialogToDisplay === GIFT_MESSAGE}
          note={cart?.note}
          onCloseClick={() => setDialogToDisplay(null)}
        />
      </>
    );
  }
);

CartDialog.propTypes = {
  /**
   * object representing a cart, retrieved from storefront
   */
  cart: CartPropTypes.cart,
  /**
   * Discount code percentage set in Contentful, should match percentage value set for that coupon code in Shopify
   */
  codeDiscountPercentage: PropTypes.number,
  /**
   * amount required before shipping is free
   */
  freeShippingThreshold: PropTypes.number,
  /**
   * Shipping method available for free shipping
   */
  freeShippingMethod: PropTypes.string,
  /**
   * Parent component maintains the open/close state and passes down
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * A function that is called to add a product to the cart.
   */
  onAddToCart: PropTypes.func.isRequired,
  /**
   * A function that is called when `CartDialog` is requesting to close.
   */
  onRequestClose: PropTypes.func.isRequired,
  /**
   * A function that is called when a ProductVariant wants to update its quantity.
   * @param {String} lineItemId
   * @param {Number} quantity
   * @param {Number} variantId
   */
  onQuantityUpdate: PropTypes.func.isRequired,
  /**
   * Controls placement of gifting upsell in cart and cart dialog
   * True = show gifting upsell at top of cart and cart dialog
   */
  prioritizedGiftingEnabled: PropTypes.bool,
  /**
   * Function to set the view to display for the coupon dialog
   */
  setViewCouponDialogDisplay: PropTypes.func.isRequired,
  /**
   * shipping and return policy from contentful
   */
  shippingPolicy: PropTypes.shape({
    cta: PropTypes.string,
    display: PropTypes.bool,
    title: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.shape({})),
    defaultSection: PropTypes.shape({})
  }),
  /**
   * Site config object from Contentful
   */
  siteConfig: PropTypes.shape({
    coupon_code: PropTypes.shape({
      value: PropTypes.string
    }),
    is_discount_auto_applied: PropTypes.shape({
      value: PropTypes.bool
    }),
    sms_terms_of_service: PropTypes.shape({
      value: PropTypes.shape({})
    }),
    coupon_code_percentage: PropTypes.shape({
      value: PropTypes.string
    })
  }),
  /**
   * Collection id for the list of gift option upsells to display
   */
  upsellCollectionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  /**
   * Subtitle for the gift option upsell collection
   */
  upsellSubtitle: PropTypes.string.isRequired,
  /**
   * Title for the gift option upsell collection
   */
  upsellTitle: PropTypes.string.isRequired
};

CartDialog.defaultProps = {
  cart: null,
  codeDiscountPercentage: 20,
  freeShippingThreshold: 50,
  freeShippingMethod: 'US',
  siteConfig: undefined,
  shippingPolicy: {},
  prioritizedGiftingEnabled: false
};

export default CartDialog;
